/**
 * DEC-589: Feature flag context.
 *
 * Loads `/api/feature-flags/me` once on auth, refetches on org switch
 * (mirrors the OrgContext refetch pattern from the v14.x dashboard fix).
 * Empty map for unauthenticated callers — all `useFeatureFlag` reads
 * return false until login.
 *
 * Consumers:
 *   const enabled = useFeatureFlag('tariff_extraction_v2');
 *   <Feature flag="x" fallback={null}>{children}</Feature>
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { featureFlagsAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { useOrg } from './OrgContext';

const FeatureFlagsContext = createContext(null);

export const FeatureFlagsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { activeOrg } = useOrg();
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setFlags({});
      return;
    }
    setLoading(true);
    try {
      const res = await featureFlagsAPI.getMine();
      setFlags(res.data?.flags || {});
    } catch {
      // Failure mode: leave existing flags in place rather than yanking
      // features mid-session. /me is idempotent — it'll succeed next mount.
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Refetch whenever the auth state OR active org changes — org-scoped
  // grants resolve differently when switching orgs.
  useEffect(() => {
    refresh();
  }, [refresh, activeOrg?.id]);

  const value = useMemo(() => ({
    flags,
    loading,
    refresh,
    isEnabled: (key) => !!flags[key],
  }), [flags, loading, refresh]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return ctx;
};

/**
 * Returns boolean for a single flag key.
 * Returns false when the user is unauthenticated, the flag doesn't exist,
 * or the response hasn't loaded yet — callers should treat the boolean as
 * authoritative without checking loading state.
 */
export const useFeatureFlag = (key) => {
  const { flags } = useFeatureFlags();
  return !!flags[key];
};

/**
 * Render-prop component:
 *   <Feature flag="x">{enabledContent}</Feature>
 *   <Feature flag="x" fallback={<Disabled />}>{enabledContent}</Feature>
 */
export const Feature = ({ flag, fallback = null, children }) => {
  const enabled = useFeatureFlag(flag);
  return enabled ? <>{children}</> : <>{fallback}</>;
};

export default FeatureFlagsContext;
